import { useCallback, useEffect, useRef, useState } from 'react';
import type { ClipboardEvent, Dispatch, SetStateAction } from 'react';
import type { ConversationAgent, ConversationTurnStatus } from '@niuma/protocol';
import type { HubApi } from '../lib/api.js';
import { bytesToBase64, stringToBase64 } from '../lib/base64.js';
import {
  conversationSubmitKey,
  expectedSubmissionDisposition,
  type SubmissionDisposition,
  type SubmissionIntent,
} from '../lib/conversation-submission.js';

interface Props {
  api: HubApi;
  machineId: string;
  sessionId: string;
  agent?: ConversationAgent;
  turnStatus?: ConversationTurnStatus | 'unknown';
  onSent?: (text: string, disposition: SubmissionDisposition) => void;
}

type AttachmentStatus = 'uploading' | 'uploaded' | 'failed';
type AttachmentKind = 'image' | 'file';
const MAX_ATTACHMENT_BYTES = 8 * 1024 * 1024;
const TARGET_ATTACHMENT_BYTES = 7.5 * 1024 * 1024;
const MAX_ATTACHMENTS_PER_KIND = 4;
const IMAGE_ACCEPT = 'image/gif,image/heic,image/heif,image/jpeg,image/png,image/webp';

interface AttachmentItem {
  id: string;
  kind: AttachmentKind;
  name: string;
  status: AttachmentStatus;
  path?: string;
  error?: string;
  upload: Promise<{ path: string; relativePath: string }>;
}

export function SessionComposer({
  api,
  machineId,
  sessionId,
  agent,
  turnStatus = 'unknown',
  onSent,
}: Props): JSX.Element {
  const mobileInputRef = useRef<HTMLTextAreaElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [mobileInput, setMobileInput] = useState('');
  const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [sendingIntent, setSendingIntent] = useState<SubmissionIntent | null>(null);

  const sending = sendingIntent !== null;
  const canSteer = agent === 'codex' && turnStatus === 'responding';

  const imageAttachments = attachments.filter((attachment) => attachment.kind === 'image');
  const fileAttachments = attachments.filter((attachment) => attachment.kind === 'file');
  const uploadingImages = imageAttachments.some((attachment) => attachment.status === 'uploading');
  const uploadingFiles = fileAttachments.some((attachment) => attachment.status === 'uploading');
  const hasFailedAttachment = attachments.some((attachment) => attachment.status === 'failed');

  const sendText = useCallback(
    async (text: string): Promise<void> => {
      if (!text) return;
      await api.sendInput(machineId, sessionId, stringToBase64(text));
    },
    [api, machineId, sessionId],
  );

  useEffect(() => {
    const input = mobileInputRef.current;
    if (!input) return;
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, 180)}px`;
  }, [mobileInput]);

  useEffect(() => {
    setAttachments([]);
    setAttachmentError(null);
  }, [machineId, sessionId]);

  const sendMobileInput = useCallback(
    async (intent: SubmissionIntent): Promise<void> => {
      if (sending || (!mobileInput.trim() && attachments.length === 0)) return;
      setAttachmentError(null);
      if (hasFailedAttachment) {
        setAttachmentError('请先移除上传失败的附件。');
        return;
      }
      setSendingIntent(intent);
      try {
        const uploaded = await Promise.all(
          attachments.map(async (attachment) => ({
            kind: attachment.kind,
            ...(await attachment.upload),
          })),
        );
        const uploadedImages = uploaded.filter((attachment) => attachment.kind === 'image');
        const uploadedFiles = uploaded.filter((attachment) => attachment.kind === 'file');
        const imageText =
          uploadedImages.length > 0
            ? `Images attached:\n${uploadedImages.map((file) => `- ${file.path}`).join('\n')}`
            : '';
        const fileText =
          uploadedFiles.length > 0
            ? `Files attached:\n${uploadedFiles.map((file) => `- ${file.path}`).join('\n')}`
            : '';
        const text = [mobileInput.trimEnd(), imageText, fileText].filter(Boolean).join('\n\n');
        await sendText(text);
        await delay(30);
        // Codex owns the actual FIFO. Tab queues while a turn is active and
        // submits immediately while idle; Enter explicitly steers an active turn.
        const submitKey = conversationSubmitKey(agent, intent);
        await sendText(submitKey);
        const disposition = expectedSubmissionDisposition(agent, turnStatus, intent);
        onSent?.(text, disposition);
        setMobileInput('');
        setAttachments([]);
      } catch (error) {
        setAttachmentError(error instanceof Error ? error.message : String(error));
      } finally {
        setSendingIntent(null);
      }
    },
    [agent, attachments, hasFailedAttachment, mobileInput, onSent, sendText, sending, turnStatus],
  );

  const addAttachments = useCallback(
    (kind: AttachmentKind, files: FileList | readonly File[] | null): void => {
      if (!files) return;
      setAttachmentError(null);
      const selected = [...files];
      const valid = selected.filter((file) =>
        kind === 'image' ? isSupportedImageFile(file) : !isSupportedImageFile(file),
      );
      if (valid.length !== selected.length) {
        setAttachmentError(kind === 'image' ? '这里只能上传图片。' : '图片请使用“图片”按钮上传。');
      }

      const currentCount = attachments.filter((attachment) => attachment.kind === kind).length;
      const slots = Math.max(0, MAX_ATTACHMENTS_PER_KIND - currentCount);
      const nextFiles = valid.slice(0, slots);
      if (valid.length > slots) {
        setAttachmentError(`最多只能附加 4 个${kind === 'image' ? '图片' : '文件'}。`);
      }
      const nextItems = nextFiles.map((file) =>
        createAttachmentItem(kind, api, machineId, sessionId, file, setAttachments),
      );
      setAttachments((prev) => [...prev, ...nextItems]);
    },
    [api, attachments, machineId, sessionId],
  );

  const pasteImages = useCallback(
    (event: ClipboardEvent<HTMLTextAreaElement>): void => {
      const images = imageFilesFromClipboard(event.clipboardData);
      if (images.length === 0) return;

      // An image-only paste has no useful textarea default. Keep the native
      // behavior when text is also present so rich clipboard content still
      // inserts its plain-text representation.
      if (!event.clipboardData.types.includes('text/plain')) {
        event.preventDefault();
      }
      addAttachments('image', images);
    },
    [addAttachments],
  );

  return (
    <form
      className="mobile-terminal-input"
      onSubmit={(event) => {
        event.preventDefault();
        void sendMobileInput('default');
      }}
    >
      {attachments.length > 0 && (
        <div className="attachment-list">
          {imageAttachments.length > 0 && (
            <AttachmentGroup
              label="图片"
              attachments={imageAttachments}
              onRemove={(id) =>
                setAttachments((prev) => prev.filter((attachment) => attachment.id !== id))
              }
            />
          )}
          {fileAttachments.length > 0 && (
            <AttachmentGroup
              label="文件"
              attachments={fileAttachments}
              onRemove={(id) =>
                setAttachments((prev) => prev.filter((attachment) => attachment.id !== id))
              }
            />
          )}
        </div>
      )}
      {attachmentError && <div className="attachment-error">{attachmentError}</div>}
      <textarea
        ref={mobileInputRef}
        value={mobileInput}
        rows={3}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        placeholder="输入文字，也可粘贴图片"
        onChange={(event) => setMobileInput(event.target.value)}
        onPaste={pasteImages}
      />
      <input
        ref={imageInputRef}
        className="attachment-file-input"
        type="file"
        accept={IMAGE_ACCEPT}
        multiple
        onChange={(event) => {
          addAttachments('image', event.target.files);
          event.target.value = '';
        }}
      />
      <input
        ref={fileInputRef}
        className="attachment-file-input"
        type="file"
        multiple
        onChange={(event) => {
          addAttachments('file', event.target.files);
          event.target.value = '';
        }}
      />
      <div className="composer-actions">
        <button
          type="button"
          onClick={() => imageInputRef.current?.click()}
          disabled={sending || imageAttachments.length >= MAX_ATTACHMENTS_PER_KIND}
          aria-label="从系统照片中选择图片"
        >
          {uploadingImages ? '上传中' : '图片'}
        </button>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={sending || fileAttachments.length >= MAX_ATTACHMENTS_PER_KIND}
          aria-label="从系统文件中选择附件"
        >
          {uploadingFiles ? '上传中' : '文件'}
        </button>
        {canSteer && (
          <button
            className="steer-now"
            type="button"
            disabled={
              sending || hasFailedAttachment || (!mobileInput.trim() && attachments.length === 0)
            }
            onClick={() => void sendMobileInput('steer')}
          >
            {sendingIntent === 'steer' ? '改向中' : '立即改向'}
          </button>
        )}
        <button
          className="primary composer-submit"
          type="submit"
          disabled={
            sending || hasFailedAttachment || (!mobileInput.trim() && attachments.length === 0)
          }
        >
          {sendingIntent === 'default'
            ? canSteer
              ? '排队中'
              : '发送中'
            : canSteer
              ? '排队'
              : '发送'}
        </button>
      </div>
    </form>
  );
}

function createAttachmentItem(
  kind: AttachmentKind,
  api: HubApi,
  machineId: string,
  sessionId: string,
  file: File,
  updateAttachments: Dispatch<SetStateAction<AttachmentItem[]>>,
): AttachmentItem {
  const id = `${file.name}:${file.lastModified}:${file.size}:${crypto.randomUUID()}`;
  const upload = file
    .arrayBuffer()
    .then((buffer) =>
      kind === 'image' ? prepareImageForUpload(file, buffer) : prepareFileForUpload(file, buffer),
    )
    .then((prepared) =>
      api.uploadAttachment(
        machineId,
        sessionId,
        prepared.fileName,
        prepared.mimeType,
        bytesToBase64(new Uint8Array(prepared.buffer)),
      ),
    )
    .then((result) => {
      updateAttachments((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, status: 'uploaded', path: result.path } : item,
        ),
      );
      return result;
    })
    .catch((error: unknown) => {
      updateAttachments((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                status: 'failed',
                error: error instanceof Error ? error.message : String(error),
              }
            : item,
        ),
      );
      throw error;
    });
  void upload.catch(() => undefined);

  return {
    id,
    kind,
    name: file.name,
    status: 'uploading',
    upload,
  };
}

function prepareFileForUpload(
  file: File,
  buffer: ArrayBuffer,
): { fileName: string; mimeType: string; buffer: ArrayBuffer } {
  if (buffer.byteLength > MAX_ATTACHMENT_BYTES) {
    throw new Error('文件太大，不能超过 8 MB。');
  }
  return {
    fileName: file.name,
    mimeType: file.type || 'application/octet-stream',
    buffer,
  };
}

async function prepareImageForUpload(
  file: File,
  buffer: ArrayBuffer,
): Promise<{ fileName: string; mimeType: string; buffer: ArrayBuffer }> {
  const mimeType = file.type || mimeTypeFromFileName(file.name) || 'application/octet-stream';
  if (buffer.byteLength <= MAX_ATTACHMENT_BYTES) {
    return { fileName: file.name, mimeType, buffer };
  }

  const compressed = await compressImage(file);
  if (compressed.buffer.byteLength > MAX_ATTACHMENT_BYTES) {
    throw new Error('图片太大，压缩后仍超过 8 MB。');
  }
  return compressed;
}

async function compressImage(
  file: File,
): Promise<{ fileName: string; mimeType: string; buffer: ArrayBuffer }> {
  const image = await loadImage(file);
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) throw new Error('浏览器不支持图片压缩。');

  const maxSide = 2400;
  const scale = Math.min(1, maxSide / Math.max(image.naturalWidth, image.naturalHeight));
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  let quality = 0.9;
  let blob = await canvasToBlob(canvas, 'image/jpeg', quality);
  while (blob.size > TARGET_ATTACHMENT_BYTES && quality > 0.55) {
    quality -= 0.1;
    blob = await canvasToBlob(canvas, 'image/jpeg', quality);
  }

  return {
    fileName: replaceExtension(file.name, '.jpg'),
    mimeType: 'image/jpeg',
    buffer: await blob.arrayBuffer(),
  };
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('无法读取这张图片，请换一张或先转成 JPG/PNG。'));
    };
    image.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('图片压缩失败。'));
      },
      type,
      quality,
    );
  });
}

function mimeTypeFromFileName(fileName: string): string | null {
  const ext = fileName.toLowerCase().match(/\.[^.]+$/)?.[0];
  switch (ext) {
    case '.gif':
      return 'image/gif';
    case '.heic':
      return 'image/heic';
    case '.heif':
      return 'image/heif';
    case '.jpg':
    case '.jpeg':
      return 'image/jpeg';
    case '.png':
      return 'image/png';
    case '.webp':
      return 'image/webp';
    default:
      return null;
  }
}

function isSupportedImageFile(file: File): boolean {
  return (
    IMAGE_ACCEPT.split(',').includes(file.type.toLowerCase()) ||
    mimeTypeFromFileName(file.name) !== null
  );
}

function imageFilesFromClipboard(clipboardData: DataTransfer): File[] {
  const itemFiles = [...clipboardData.items]
    .filter((item) => item.kind === 'file' && item.type.toLowerCase().startsWith('image/'))
    .map((item) => item.getAsFile())
    .filter((file): file is File => file !== null);

  if (itemFiles.length > 0) return itemFiles;

  // Older Safari versions may expose clipboard files without corresponding
  // DataTransferItems.
  return [...clipboardData.files].filter(
    (file) => file.type.toLowerCase().startsWith('image/') || mimeTypeFromFileName(file.name),
  );
}

function replaceExtension(fileName: string, extension: string): string {
  const base = fileName.replace(/\.[^/.]+$/, '');
  return `${base || 'image'}${extension}`;
}

function attachmentStatusLabel(attachment: AttachmentItem): string {
  switch (attachment.status) {
    case 'uploading':
      return '上传中';
    case 'uploaded':
      return '已上传';
    case 'failed':
      return attachment.error ? `失败：${attachment.error}` : '失败';
  }
}

function AttachmentGroup({
  label,
  attachments,
  onRemove,
}: {
  label: string;
  attachments: AttachmentItem[];
  onRemove: (id: string) => void;
}): JSX.Element {
  return (
    <div className="attachment-group">
      <strong>{label}</strong>
      <div className="attachment-chips">
        {attachments.map((attachment) => (
          <span className="attachment-chip" key={attachment.id}>
            <span>{attachment.name}</span>
            <small title={attachment.error}>{attachmentStatusLabel(attachment)}</small>
            <button
              type="button"
              onClick={() => onRemove(attachment.id)}
              aria-label={`移除 ${attachment.name}`}
            >
              ×
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}
